import type { DevView } from './console.ts'
import type { ShopsView, WalletView } from './economy.ts'
import type { HudView } from './hud.ts'
import type { BindingLabel, ButtonActionId, InputDevice } from './input.ts'
import type { InventoryState } from './inventory.ts'
import type { MessagesView } from './messages.ts'
import type { OsView } from './os.ts'
import type { SavesView } from './saves.ts'
import type { StatusMeterView } from './status.ts'
import type { TaskView } from './tasks.ts'
import { createDefaultSettings } from './settings.ts'
import type { KeyboardBindable, Settings } from './settings.ts'
import type { LocalizedText } from './text.ts'
import type { WelfareView } from './welfare.ts'

/** App phases. The engine's phase machine owns the allowed transitions between them. */
export type GamePhase = 'boot' | 'loading' | 'menu' | 'playing' | 'paused' | 'error'

/**
 * Everything the UI renders from, as one immutable snapshot. The engine replaces it
 * (never mutates it) whenever something changes.
 */
export interface ViewState {
  readonly phase: GamePhase
  /** Engine boot, before any level loads. `progress` runs from 0 to 1. */
  readonly boot: { readonly progress: number }
  /** Set while a level loads, `null` otherwise. `progress` runs from 0 to 1. */
  readonly loading: {
    readonly levelId: string
    /** `null` until the level's definition has loaded. */
    readonly levelName: LocalizedText | null
    readonly progress: number
    readonly step: LocalizedText
  } | null
  /** The level currently loaded. */
  readonly level: { readonly id: string; readonly name: LocalizedText } | null
  /** Set when `phase` is `'error'`. `detail` is technical text for bug reports. */
  readonly error: { readonly title: LocalizedText; readonly detail: string } | null
  readonly input: {
    /** The device that produced the last meaningful input. */
    readonly device: InputDevice
    readonly pointerLocked: boolean
    /** The device has a touch screen, so the touch controls can show. */
    readonly touchAvailable: boolean
    /** The first key and gamepad button bound to each action, for key hints. */
    readonly labels: Readonly<Record<ButtonActionId, BindingLabel>>
    /** Effective key and mouse-button codes for every rebindable action. */
    readonly keyboard: Readonly<Record<KeyboardBindable, readonly string[]>>
  }
  readonly hud: HudView
  /** The player's QuickAccess and the open inventory screen, or null with no player. */
  readonly inventory: InventoryState | null
  /**
   * The player's meters to show, in order, or null with no player. A high-is-bad meter
   * (intoxication) at 0 is left out.
   */
  readonly status: readonly StatusMeterView[] | null
  /** The player's money, or null with no level loaded. */
  readonly wallet: WalletView | null
  /** The task list in its order (missions first); empty with no level loaded. */
  readonly tasks: readonly TaskView[]
  /** The shops, the player's orders and the last checkout. */
  readonly shops: ShopsView
  /** The player's Wassup chats, the latest first; empty with no level loaded. */
  readonly messages: MessagesView
  /** The weekly Wellfare application, or null in a level without a clock (or none loaded). */
  readonly welfare: WelfareView | null
  /** The device showing the OS, and the clock it shows. */
  readonly os: OsView
  /** The save slots, the one being played and the last save. */
  readonly saves: SavesView
  /** The player settings currently in effect. */
  readonly settings: Settings
  /** The graphics renderer was changed and a reload is needed to apply it. */
  readonly restartRequired: boolean
  /** A one-line message for the main menu, such as a WebGPU fallback. */
  readonly notice: LocalizedText | null
  /** Smoothed frames per second, set only while the FPS counter is on. */
  readonly performance: { readonly fps: number } | null
  /** Developer console and stats. Hidden until dev mode is on. */
  readonly dev: DevView
}

const NO_LABEL: BindingLabel = { keyboard: '', gamepad: '' }
const NO_KEYS: readonly string[] = []

export const initialViewState: ViewState = {
  phase: 'boot',
  boot: { progress: 0 },
  loading: null,
  level: null,
  error: null,
  input: {
    device: 'keyboardMouse',
    pointerLocked: false,
    touchAvailable: false,
    labels: {
      jump: NO_LABEL,
      sprint: NO_LABEL,
      pause: NO_LABEL,
      interact: NO_LABEL,
      skip: NO_LABEL,
      useItem: NO_LABEL,
      drop: NO_LABEL,
      inventory: NO_LABEL,
      phone: NO_LABEL,
    },
    keyboard: {
      moveForward: NO_KEYS,
      moveBack: NO_KEYS,
      moveLeft: NO_KEYS,
      moveRight: NO_KEYS,
      jump: NO_KEYS,
      sprint: NO_KEYS,
      interact: NO_KEYS,
      skip: NO_KEYS,
      useItem: NO_KEYS,
      drop: NO_KEYS,
      inventory: NO_KEYS,
      phone: NO_KEYS,
    },
  },
  hud: {
    prompt: null,
    targetFocused: false,
    holding: false,
    card: null,
    subtitle: null,
    skip: null,
    hold: null,
    takeover: false,
    eyelids: { closed: false, seconds: 0, fade: false },
  },
  inventory: null,
  status: null,
  wallet: null,
  tasks: [],
  shops: { available: false, vendors: [], orders: [], checkout: null },
  messages: { chats: [], unread: 0 },
  welfare: null,
  os: { open: null, time: null, day: null },
  saves: { slots: [], active: null, lastSaved: null, unsaved: false },
  // Replaced as soon as the engine loads the saved settings. No `window` access here.
  settings: createDefaultSettings({
    dev: false,
    touch: false,
    lowEnd: false,
    prefersReducedMotion: false,
  }),
  restartRequired: false,
  notice: null,
  performance: null,
  dev: { enabled: false, consoleOpen: false, lines: [], statsVisible: false, stats: null },
}
